import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import InitialNodeGroup from './InitialNodeGroup';

/**
 * 引導單元列表組件（支持分組和拖拉）
 * @param {Array} groups - 群組陣列
 * @param {Function} onAddGroup - 新增群組回調
 * @param {Function} onUpdateGroup - 更新群組回調
 * @param {Function} onRemoveGroup - 刪除群組回調
 * @param {Function} onAddNode - 新增節點到群組回調
 * @param {Function} onUpdateNode - 更新節點回調
 * @param {Function} onRemoveNode - 刪除節點回調
 * @param {Function} onReorderGroups - 重新排序群組回調
 * @param {Function} onReorderNodes - 重新排序節點回調
 * @param {Function} onMoveNodeBetweenGroups - 在群組間移動節點回調
 * @param {Object} nodePlan - 節點計劃配置
 * @param {string} activityContent - 活動內容（用於提示）
 */
const InitialNodeList = ({
  groups = [],
  compact = false,
  onAddGroup,
  onUpdateGroup,
  onRemoveGroup,
  onAddNode,
  onUpdateNode,
  onRemoveNode,
  onReorderGroups,
  onReorderNodes,
  onMoveNodeBetweenGroups,
  nodePlan,
  activityContent
}) => {
  const [showInstructions, setShowInstructions] = useState(true);
  
  const handleDragEnd = (result) => {
    const { source, destination, type } = result;

    // 沒有目的地，放棄操作
    if (!destination) return;

    // 沒有移動
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    // 移動群組
    if (type === 'group') {
      onReorderGroups(source.index, destination.index);
      return;
    }

    // 移動節點
    if (type === 'node') {
      // 在同一群組內移動
      if (source.droppableId === destination.droppableId) {
        onReorderNodes(source.droppableId, source.index, destination.index);
      } else {
        // 在不同群組間移動
        const sourceGroup = groups.find(g => g.id === source.droppableId);
        const nodeToMove = sourceGroup?.nodes[source.index];
        if (nodeToMove) {
          onMoveNodeBetweenGroups(
            source.droppableId,
            destination.droppableId,
            nodeToMove.id
          );
        }
      }
    }
  };

  return (
    <Box sx={{ mt: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{compact ? '活動' : '引導單元設定'}</Typography>
        {compact && (
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, ml: 2 }}>
            ⇅ 拖曳排序
          </Typography>
        )}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAddGroup}
        >
          {compact ? '新增活動' : '新增單元'}
        </Button>
      </Box>
      
      {activityContent && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>提示：</strong>單元會自動整合到活動之中，小組成員可以在小組面板中使用這些節點來刺激討論。
          </Typography>
        </Alert>
      )}

      {showInstructions && (
        <Alert 
          severity="info" 
          sx={{ mb: 2 }}
          onClose={() => setShowInstructions(false)}
        >
          <Typography variant="body2">
            <strong>使用說明：</strong>
          </Typography>
          <Typography variant="body2" component="ul" sx={{ mt: 1, pl: 2 }}>
            <li>「開始引導」群組的節點會在活動開始時自動顯示（此群組名稱不可修改）</li>
            <li>其他群組的節點可透過小組工具手動添加到當前小組中</li>
            <li>使用拖拉功能可以重新排序群組和節點</li>
            <li>節點可以在群組之間拖拉移動</li>
          </Typography>
        </Alert>
      )}
      
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="all-groups" type="group">
          {(provided) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {groups.map((group, index) => (
                <InitialNodeGroup
                  key={group.id}
                  group={group}
                  index={index}
                  onUpdateGroup={onUpdateGroup}
                  onRemoveGroup={onRemoveGroup}
                  onAddNode={onAddNode}
                  onUpdateNode={onUpdateNode}
                  onRemoveNode={onRemoveNode}
                  nodePlan={nodePlan}
                  compact={compact}
                />
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>
      
      {groups.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          目前沒有群組，點擊「新增群組」來創建第一個群組。
        </Alert>
      )}
    </Box>
  );
};

export default InitialNodeList;
